import React, { useEffect, useRef, useState } from 'react';
import { Chart } from 'chart.js/auto';
import apiService from '../services/apiService';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import chartCache from '../utils/chartCache';
import './CustomLegend.css';
import bmailogo from '../assets/bmailogo.png';

// Helper function to capitalize the first letter of domain name
const capitalizeFirstLetter = (domain) => {
  if (!domain) return '';
  return domain.charAt(0).toUpperCase() + domain.slice(1);
};

// Global request registry to prevent duplicate requests across component instances
const activeRequests = new Map();

const OpportunityChart = ({ submissionId, companyDomain, isPortfolio = false, isPublic = false, preloadedData = null, isResultsView = false, submissionData = null, selectedSubmissions = null }) => {
  const chartContainerRef = useRef(null);
  const chartRef = useRef(null);
  const [chartData, setChartData] = useState(preloadedData);
  const [loading, setLoading] = useState(!preloadedData);
  const [error, setError] = useState(null);
  const [expandedDescriptions, setExpandedDescriptions] = useState({});
  const requestInProgressRef = useRef(false); // Prevent duplicate requests

  // Debug logging
  console.log('OpportunityChart props:', { submissionId, companyDomain, isPortfolio, isPublic, preloadedData: !!preloadedData, isResultsView, selectedSubmissions });

  useEffect(() => {
    const fetchChartData = async () => {
      // Skip API call if data is already provided
      if (preloadedData) {
        setChartData(preloadedData);
        setLoading(false);
        return;
      }
      
      // Handle selectedSubmissions (combined chart) - get portfolio data and filter
      if (selectedSubmissions && selectedSubmissions.length > 0) {
        try {
          setLoading(true);
          console.log('Fetching data for selected submissions:', selectedSubmissions);
          
          // Fetch portfolio data which contains all submissions
          const portfolioData = await apiService.getPortfolioChart();
          
          // Filter to only show the selected submissions
          const filteredData = {
            ...portfolioData,
            categorized_data: {}
          };
          
          Object.keys(portfolioData.categorized_data).forEach(category => {
            const items = portfolioData.categorized_data[category];
            filteredData.categorized_data[category] = items.filter(
              item => selectedSubmissions.includes(String(item.submission_id)) || 
                      selectedSubmissions.includes(Number(item.submission_id))
            );
          });
          
          console.log('Filtered data for selected submissions:', filteredData);
          setChartData(filteredData);
          setLoading(false);
          return;
        } catch (err) {
          console.error('Error fetching selected submissions data:', err);
          setError('Failed to load chart data for selected surveys. Please try again.');
          setLoading(false);
          return;
        }
      }
      
      // Check cache first
      const cachedData = chartCache.get(submissionId, companyDomain, isPortfolio, isPublic);
      if (cachedData) {
        console.log('Using cached chart data');
        setChartData(cachedData);
        setLoading(false);
        return;
      }
      
      // Create request key for deduplication
      const requestKey = chartCache.generateKey(submissionId, companyDomain, isPortfolio, isPublic);
      
      // Check if request is already in progress globally
      if (activeRequests.has(requestKey)) {
        console.log('Request already in progress globally, waiting for result');
        try {
          const result = await activeRequests.get(requestKey);
          setChartData(result);
          setLoading(false);
          return;
        } catch (err) {
          console.error('Shared request failed:', err);
          setError('Failed to load chart data. Please try again.');
          setLoading(false);
          return;
        }
      }
      
      // Prevent duplicate requests from same component
      if (requestInProgressRef.current) {
        console.log('Request already in progress, skipping duplicate call');
        return;
      }
      
      // Create and register the request promise
      const requestPromise = (async () => {
        try {
          requestInProgressRef.current = true;
          setLoading(true);
          
          // Fetch data using apiService
          let responseData;
          if (isPortfolio) {
            responseData = await apiService.getPortfolioChart();
          } else if (companyDomain) {
            responseData = await apiService.getCompanyChart(companyDomain);
          } else if (isPublic) {
            responseData = await apiService.getPublicChartData(submissionId);
          } else {
            responseData = await apiService.getAdminChartData(submissionId);
          }
          
          let finalData;
          
          // For single submission view, the API already returns filtered data
          // so we don't need to filter again
          finalData = responseData;
          console.log('📊 Final chart data:', finalData);
          
          // Cache the data before setting it
          chartCache.set(submissionId, companyDomain, isPortfolio, isPublic, finalData);
          
          return finalData;
        } catch (err) {
          console.error('Error fetching chart data:', err);
          throw new Error('Failed to load chart data. Please try again.');
        }
      })();
      
      // Register the request globally
      activeRequests.set(requestKey, requestPromise);
      
      try {
        const finalData = await requestPromise;
        setChartData(finalData);
        setError(null);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
        requestInProgressRef.current = false;
        // Clean up the request from registry
        activeRequests.delete(requestKey);
      }
    };

    if (submissionId || companyDomain || isPortfolio || (selectedSubmissions && selectedSubmissions.length > 0)) {
      fetchChartData();
    }
  }, [submissionId, companyDomain, isPortfolio, isPublic, preloadedData, selectedSubmissions]);

  useEffect(() => {
    if (!chartData || !chartContainerRef.current) return;

    // Destroy previous chart if it exists
    if (chartRef.current) {
      chartRef.current.destroy();
      chartRef.current = null;
    }

    // Clear container completely
    chartContainerRef.current.innerHTML = '';
    
    // Create fresh canvas element
    const canvas = document.createElement('canvas');
    canvas.id = 'opportunityChart';
    canvas.style.width = '100%';
    canvas.style.height = '100%';
    chartContainerRef.current.appendChild(canvas);

    const ctx = canvas.getContext('2d');
    
    // Create datasets from categorized data
    const datasets = [];
    
    // Updated color mapping to match the backend
    const updatedColors = {
      "Quick Wins": "#36b37e", // Green
      "Strategic Challenges": "#ff5630", // Red-Orange
      "Technical Experiments": "#0065ff", // Blue
      "Lower Priority": "#6b778c" // Gray
    };
    
    Object.entries(chartData.categorized_data || {}).forEach(([category, items]) => {
      if (items.length === 0) return;
      
      datasets.push({
        label: category,
        data: items.map(item => ({
          x: item.x,
          y: item.y,
          r: item.r || 5, // Default radius if missing
          ...item // Pass through all properties for tooltip access
        })),
        backgroundColor: items.map(item => {
          const color = updatedColors[category] || chartData.colors[category];
          return item.highlighted 
            ? `${color}E6` // 90% opacity
            : `${color}B3`; // 70% opacity
        }),
        borderColor: items.map(() => updatedColors[category] || chartData.colors[category]),
        borderWidth: items.map(item => item.highlighted ? 2 : 1),
        hoverBorderWidth: 2,
        hoverBackgroundColor: items.map(item => {
          const color = updatedColors[category] || chartData.colors[category];
          return `${color}`;
        }),
        pointHoverRadius: 7 // Slightly larger on hover
      });
    });

    // Create the chart
    chartRef.current = new Chart(ctx, {
      type: 'bubble',
      data: { datasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        layout: {
          padding: {
            top: 25,
            right: 25,
            bottom: 25,
            left: 25
          }
        },
        scales: {
          x: {
            title: {
              display: true,
              text: 'ROI Potential',
              font: { weight: 'bold', family: 'Arial', size: 15 },
              padding: { top: 15 },
              color: '#333'
            },
            min: -10,
            max: 10,
            grid: {
              color: 'rgba(200, 200, 200, 0.15)',
              lineWidth: 1,
              drawBorder: false
            },
            border: {
              display: true,
              color: 'rgba(0, 0, 0, 0.9)',
              width: 3
            },
            ticks: {
              color: '#444',
              font: {
                family: 'Arial',
                size: 11,
                weight: '500'
              },
              maxRotation: 0,
              padding: 8,
              autoSkip: false,
              callback: function(value) {
                if (value === -8) return 'Very Low';
                if (value === 0) return 'Neutral';
                if (value === 8) return 'Very High';
                if (value === -10 || value === 10) return '';
                if (value % 2 === 0) return value;
                return '';
              }
            },
            position: 'center',
            beginAtZero: false
          },
          y: {
            title: {
              display: true,
              text: 'Tech Feasibility',
              font: { weight: 'bold', family: 'Arial', size: 15 },
              padding: { bottom: 15 },
              color: '#333'
            },
            min: -10,
            max: 10,
            grid: {
              color: 'rgba(200, 200, 200, 0.15)',
              lineWidth: 1,
              drawBorder: false
            },
            border: {
              display: true,
              color: 'rgba(0, 0, 0, 0.9)',
              width: 3
            },
            ticks: {
              color: '#444',
              font: {
                family: 'Arial',
                size: 11,
                weight: '500'
              },
              padding: 8,
              autoSkip: false,
              callback: function(value) {
                if (value === -8) return 'Very Low';
                if (value === 0) return 'Neutral';
                if (value === 8) return 'Very High';
                if (value === -10 || value === 10) return '';
                if (value % 2 === 0) return value;
                return '';
              }
            },
            position: 'center',
            beginAtZero: false
          }
        },
        plugins: {
          title: {
            display: true,
            text: 'Strategic Assessment Matrix',
            font: { 
              size: 18, 
              weight: 'bold', 
              family: 'Arial'
            },
            color: '#333',
            padding: { bottom: 5 }
          },
          subtitle: {
            display: true,
            text: 'TD&R, Internal Audit & Compliance Functions',
            font: {
              size: 14,
              family: 'Arial',
              weight: 'normal'
            },
            color: '#666',
            padding: { bottom: 15 }
          },
          legend: {
            display: false // Hide default legend as we'll create a custom one
          },
          tooltip: {
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            titleFont: { size: 14, weight: 'bold', family: 'Arial' },
            bodyFont: { size: 13, family: 'Arial' },
            titleColor: '#333',
            bodyColor: '#555',
            displayColors: false,
            padding: 12,
            cornerRadius: 4,
            boxPadding: 4,
            borderColor: 'rgba(0, 0, 0, 0.1)',
            borderWidth: 1,
            callbacks: {
              title: function(tooltipItems) {
                const item = tooltipItems[0].raw;
                return item.opportunity_name || 'Unnamed Opportunity';
              },
              label: function(tooltipItem) {
                const item = tooltipItem.raw;
                // Format values with + or - signs
                const formatValue = (value) => {
                  const num = Number(value);
                  const fixed = num.toFixed(1);
                  return num > 0 ? `+${fixed}` : fixed;
                };
                
                return [
                  `Category: ${item.strategic_category || 'Not categorized'}`,
                  `ROI Potential: ${formatValue(item.x)}`,
                  `Tech Feasibility: ${formatValue(item.y)}`,
                  `Strategic Value: ${formatValue(item.r / 3)}`
                ];
              }
            }
          }
        }
      }
    });

    // Add quadrant labels and lines
    const addQuadrantElements = () => {
      const chart = chartRef.current;
      if (!chart || !chart.ctx) {
        return; // Guard against null chart or context
      }
      const ctx = chart.ctx;
      
      // Save the original state
      ctx.save();
      
      // Draw vertical & horizontal center lines
      ctx.beginPath();
      ctx.strokeStyle = 'rgba(0, 0, 0, 0.75)';
      ctx.lineWidth = 2.5;
      
      // Vertical line at x=0
      const xZero = chart.scales.x.getPixelForValue(0);
      ctx.moveTo(xZero, chart.scales.y.getPixelForValue(10));
      ctx.lineTo(xZero, chart.scales.y.getPixelForValue(-10));
      
      // Horizontal line at y=0
      const yZero = chart.scales.y.getPixelForValue(0);
      ctx.moveTo(chart.scales.x.getPixelForValue(-10), yZero);
      ctx.lineTo(chart.scales.x.getPixelForValue(10), yZero);
      
      ctx.stroke();
      
      // Don't add quadrant labels directly on the chart for a cleaner look
      // The custom legend below the chart will provide this information
      
      // Restore the state
      ctx.restore();
    };

    // Register afterDraw plugin
    const afterDrawPlugin = {
      id: 'afterDraw',
      afterDraw: (chart) => {
        if (!chart || !chart.ctx) {
          return; // Guard against null chart or context
        }
        addQuadrantElements();
      }
    };

    Chart.register(afterDrawPlugin);
  }, [chartData]);

  // Cleanup effect to destroy chart on unmount
  useEffect(() => {
    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
    };
  }, []);

  // Function to generate chart and send to backend for email (unused - using ChartEmailService instead)
  // eslint-disable-next-line no-unused-vars
  const sendChartToBackend = (submissionId, opportunityName) => {
    return new Promise((resolve, reject) => {
      if (!chartContainerRef.current || !chartData) {
        reject(new Error("Chart not available"));
        return;
      }
      
      console.log(`Generating chart for submission ${submissionId} to send to backend...`);
      
      // Create a container for the entire chart (same as download logic)
      const downloadContainer = document.createElement('div');
      downloadContainer.style.backgroundColor = '#ffffff';
      downloadContainer.style.width = '800px';
      downloadContainer.style.position = 'fixed';
      downloadContainer.style.left = '-9999px';
      downloadContainer.style.top = '0';
      downloadContainer.style.zIndex = '-1000';
      document.body.appendChild(downloadContainer);
      
      // Add header
      const headerDiv = document.createElement('div');
      headerDiv.style.background = '#000000';
      headerDiv.style.color = 'white';
      headerDiv.style.padding = '20px';
      headerDiv.style.textAlign = 'center';
      headerDiv.innerHTML = `
        <h2 style="margin: 0; font-size: 28px; font-weight: 600; letter-spacing: 0.5px;">${getOpportunityName()}</h2>
        <div style="margin-top: 8px; font-size: 17px; opacity: 0.9;">Opportunity Survey Analysis</div>
        <div style="margin-top: 15px; font-size: 13px; line-height: 1.6; opacity: 0.85;">
          • X-axis: ROI Potential (-10 to +10)<br>
          • Y-axis: Tech Feasibility (-10 to +10)<br>
          • Bubble Size: Strategic Value (1-10)
        </div>
      `;
      downloadContainer.appendChild(headerDiv);
      
      // Create chart wrapper
      const chartClone = document.createElement('div');
      chartClone.style.padding = '20px';
      chartClone.style.height = '450px';
      chartClone.style.width = '760px';
      chartClone.style.position = 'relative';
      
      const newCanvas = document.createElement('canvas');
      newCanvas.id = 'emailChart';
      newCanvas.width = 760;
      newCanvas.height = 450;
      newCanvas.style.width = '100%';
      newCanvas.style.height = '100%';
      chartClone.appendChild(newCanvas);
      downloadContainer.appendChild(chartClone);
      
      // Add scores section for single assessment
      const scores = getScores();
      if (scores && !isPortfolio) {
        const scoresDiv = document.createElement('div');
        scoresDiv.style.display = 'flex';
        scoresDiv.style.gap = '15px';
        scoresDiv.style.padding = '15px';
        scoresDiv.style.backgroundColor = '#f5f8fa';
        scoresDiv.style.borderTop = '1px solid #e0e7ee';
        
        // Add score cards (same as download logic)
        scoresDiv.innerHTML = `
          <div style="flex: 1; background-color: white; border: 1px solid #dee2e6; border-radius: 8px; padding: 15px;">
            <h3 style="font-size: 16px; margin-top: 0; margin-bottom: 12px; color: #555; border-bottom: 2px solid #36b37e; padding-bottom: 8px;">Tech Feasibility Assessment</h3>
            <div style="font-size: 24px; font-weight: bold; margin: 15px 0; text-align: center; color: #36b37e;">+${scores.tech}</div>
            <div style="font-size: 13px; line-height: 1.4;"><ul style="padding-left: 18px; margin: 8px 0;"><li style="margin-bottom: 5px;">✓ Predictable patterns in process flow</li><li style="margin-bottom: 5px;">✓ Cloud services acceptable for implementation</li><li style="margin-bottom: 5px;">✓ APIs available for required systems</li><li style="margin-bottom: 5px;">✓ Well-documented requirements</li></ul></div>
          </div>
          <div style="flex: 1; background-color: white; border: 1px solid #dee2e6; border-radius: 8px; padding: 15px;">
            <h3 style="font-size: 16px; margin-top: 0; margin-bottom: 12px; color: #555; border-bottom: 2px solid #0065ff; padding-bottom: 8px;">ROI Potential</h3>
            <div style="font-size: 24px; font-weight: bold; margin: 15px 0; text-align: center; color: #0065ff;">+${scores.roi}</div>
            <div style="font-size: 13px; line-height: 1.4;"><ul style="padding-left: 18px; margin: 8px 0;"><li style="margin-bottom: 5px;">✓ High daily transaction volume</li><li style="margin-bottom: 5px;">✓ 25% time savings expected</li><li style="margin-bottom: 5px;">✓ Moderate change management needed</li><li style="margin-bottom: 5px;">✓ Significant risk reduction potential</li></ul></div>
          </div>
          <div style="flex: 1; background-color: white; border: 1px solid #dee2e6; border-radius: 8px; padding: 15px;">
            <h3 style="font-size: 16px; margin-top: 0; margin-bottom: 12px; color: #555; border-bottom: 2px solid #ff5630; padding-bottom: 8px;">Strategic Value</h3>
            <div style="font-size: 24px; font-weight: bold; margin: 15px 0; text-align: center; color: #ff5630;">${scores.strategic}</div>
            <div style="font-size: 13px; line-height: 1.4;"><ul style="padding-left: 18px; margin: 8px 0;"><li style="margin-bottom: 5px;">✓ Direct alignment with business objectives</li><li style="margin-bottom: 5px;">✓ Significant efficiency improvements</li><li style="margin-bottom: 5px;">✓ Core operational excellence</li></ul></div>
          </div>
        `;
        downloadContainer.appendChild(scoresDiv);
      }
      
      // Recreate chart (same logic as download)
      const recreateChart = () => {
        if (!chartData) return null;
        
        const ctx = newCanvas.getContext('2d');
        const datasets = [];
        const updatedColors = {
          "Quick Wins": "#36b37e",
          "Strategic Challenges": "#ff5630",
          "Technical Experiments": "#0065ff",
          "Lower Priority": "#6b778c"
        };
        
        Object.entries(chartData.categorized_data || {}).forEach(([category, items]) => {
          if (items.length === 0) return;
          
          datasets.push({
            label: category,
            data: items.map(item => ({
              x: item.x,
              y: item.y,
              r: item.r || 5,
              ...item
            })),
            backgroundColor: items.map(item => {
              const color = updatedColors[category] || chartData.colors[category];
              return item.highlighted ? `${color}E6` : `${color}B3`;
            }),
            borderColor: items.map(() => updatedColors[category] || chartData.colors[category]),
            borderWidth: items.map(item => item.highlighted ? 2 : 1),
          });
        });
        
        return new Chart(ctx, {
          type: 'bubble',
          data: { datasets },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            animation: false,
            layout: { padding: { top: 25, right: 25, bottom: 25, left: 25 } },
            scales: {
              x: {
                title: { display: true, text: 'ROI Potential', font: { weight: 'bold', family: 'Arial', size: 15 }, padding: { top: 15 }, color: '#333' },
                min: -10, max: 10,
                grid: { color: 'rgba(200, 200, 200, 0.15)', lineWidth: 1, drawBorder: false },
                border: { display: true, color: 'rgba(0, 0, 0, 0.9)', width: 3 },
                ticks: {
                  color: '#444', font: { family: 'Arial', size: 11, weight: '500' }, maxRotation: 0, padding: 8, autoSkip: false,
                  callback: function(value) {
                    if (value === -8) return 'Very Low';
                    if (value === 0) return 'Neutral';
                    if (value === 8) return 'Very High';
                    if (value === -10 || value === 10) return '';
                    if (value % 2 === 0) return value;
                    return '';
                  }
                },
                position: 'center', beginAtZero: false
              },
              y: {
                title: { display: true, text: 'Tech Feasibility', font: { weight: 'bold', family: 'Arial', size: 15 }, padding: { bottom: 15 }, color: '#333' },
                min: -10, max: 10,
                grid: { color: 'rgba(200, 200, 200, 0.15)', lineWidth: 1, drawBorder: false },
                border: { display: true, color: 'rgba(0, 0, 0, 0.9)', width: 3 },
                ticks: {
                  color: '#444', font: { family: 'Arial', size: 11, weight: '500' }, padding: 8, autoSkip: false,
                  callback: function(value) {
                    if (value === -8) return 'Very Low';
                    if (value === 0) return 'Neutral';
                    if (value === 8) return 'Very High';
                    if (value === -10 || value === 10) return '';
                    if (value % 2 === 0) return value;
                    return '';
                  }
                },
                position: 'center', beginAtZero: false
              }
            },
            plugins: {
              title: { display: true, text: 'Strategic Assessment Matrix', font: { size: 18, weight: 'bold', family: 'Arial' }, color: '#333', padding: { bottom: 5 } },
              subtitle: { display: true, text: 'TD&R, Internal Audit & Compliance Functions', font: { size: 14, family: 'Arial', weight: 'normal' }, color: '#666', padding: { bottom: 15 } },
              legend: { display: false }
            }
          },
          plugins: [{
            id: 'emailQuadrantLines',
            afterDraw: (chart) => {
              if (!chart || !chart.ctx) return;
              const ctx = chart.ctx;
              ctx.save();
              ctx.beginPath();
              ctx.strokeStyle = 'rgba(0, 0, 0, 0.75)';
              ctx.lineWidth = 2.5;
              const xZero = chart.scales.x.getPixelForValue(0);
              ctx.moveTo(xZero, chart.scales.y.getPixelForValue(10));
              ctx.lineTo(xZero, chart.scales.y.getPixelForValue(-10));
              const yZero = chart.scales.y.getPixelForValue(0);
              ctx.moveTo(chart.scales.x.getPixelForValue(-10), yZero);
              ctx.lineTo(chart.scales.x.getPixelForValue(10), yZero);
              ctx.stroke();
              ctx.restore();
            }
          }]
        });
      };
      
      const emailChartInstance = recreateChart();
      
      // Generate image and send to backend
      setTimeout(() => {
        html2canvas(downloadContainer, { 
          backgroundColor: '#ffffff',
          scale: 2,
          logging: false,
          width: 800,
          useCORS: true,
          allowTaint: true
        }).then(canvas => {
          // Clean up
          if (emailChartInstance) {
            emailChartInstance.destroy();
          }
          document.body.removeChild(downloadContainer);
          
          // Get base64 image (remove data:image/png;base64, prefix)
          const base64Image = canvas.toDataURL('image/png').split(',')[1];
          
          console.log('Chart generated, sending to backend...');
          
          // Send to backend
          apiService.storeChartImage({
            image_data: base64Image,
            submission_id: submissionId,
            opportunity_name: opportunityName
          }).then((response) => {
            // Chart successfully sent to backend
            resolve(base64Image);
          }).catch((error) => {
            console.error('Failed to send chart to backend:', error);
            reject(error);
          });
          
        }).catch(err => {
          console.error("Error generating chart image:", err);
          if (emailChartInstance) {
            emailChartInstance.destroy();
          }
          document.body.removeChild(downloadContainer);
          reject(err);
        });
      }, 500);
    });
  };

  // Function to download the chart as PDF
  const downloadChartAsPDF = () => {
    if (!chartContainerRef.current) return;
    
    // Create a container for the entire chart
    // A4 is 210mm x 297mm, at 72dpi that's ~595px x 842px
    // Using 842px width (landscape orientation) for better horizontal distribution
    const downloadContainer = document.createElement('div');
    downloadContainer.style.backgroundColor = '#ffffff';
    downloadContainer.style.width = '842px'; // A4 landscape width - wider format
    downloadContainer.style.margin = '0';
    downloadContainer.style.padding = '0';
    downloadContainer.style.position = 'fixed'; // Using fixed to preserve rendering
    downloadContainer.style.left = '-9999px'; // Position off-screen
    downloadContainer.style.top = '0';
    downloadContainer.style.zIndex = '-1000'; // Behind everything else
    downloadContainer.style.boxSizing = 'border-box';
    document.body.appendChild(downloadContainer);
    
    // Add header with logo
    const headerDiv = document.createElement('div');
    headerDiv.style.background = '#000000';
    headerDiv.style.color = 'white';
    headerDiv.style.padding = '20px 0';
    headerDiv.style.textAlign = 'center';
    headerDiv.style.width = '842px';
    headerDiv.style.margin = '0';
    headerDiv.style.boxSizing = 'border-box';
    headerDiv.innerHTML = `
      <img src="${bmailogo}" alt="Bytemethod AI Logo" style="width: 280px; height: auto; margin-bottom: 12px; display: inline-block;" />
      <h2 style="margin: 0; font-size: 30px; font-weight: 600; letter-spacing: 0.5px;">${getOpportunityName()}</h2>
      <div style="margin-top: 8px; font-size: 18px; opacity: 0.9;">Opportunity Survey Analysis</div>
      <div style="margin-top: 12px; font-size: 14px; line-height: 1.6; opacity: 0.85;">
        • X-axis: ROI Potential (-10 to +10)<br>
        • Y-axis: Tech Feasibility (-10 to +10)<br>
        • Bubble Size: Strategic Value (1-10)
      </div>
    `;
    downloadContainer.appendChild(headerDiv);
    
    // Create a chart wrapper to maintain proper dimensions
    const chartClone = document.createElement('div');
    chartClone.style.padding = '10px 0';
    chartClone.style.height = '480px';
    chartClone.style.width = '842px';
    chartClone.style.margin = '0';
    chartClone.style.position = 'relative';
    chartClone.style.boxSizing = 'border-box';
    
    // Create a new canvas for the chart
    const newCanvas = document.createElement('canvas');
    newCanvas.id = 'downloadChart';
    newCanvas.width = 842;  // Match container width
    newCanvas.height = 460; // Adjusted height for wider format
    newCanvas.style.width = '100%';
    newCanvas.style.height = '100%';
    chartClone.appendChild(newCanvas);
    downloadContainer.appendChild(chartClone);
    
    // Add survey cards section for portfolio and company charts
    if ((isPortfolio || companyDomain) && chartData && chartData.categorized_data) {
      // Collect all surveys across all categories
      const allSurveys = [];
      Object.entries(chartData.categorized_data).forEach(([category, items]) => {
        items.forEach(item => {
          allSurveys.push({
            ...item,
            category
          });
        });
      });

      if (allSurveys.length > 0) {
        // Add survey cards title
        const surveyCardsTitle = document.createElement('div');
        surveyCardsTitle.style.padding = '15px 0 10px';
        surveyCardsTitle.style.backgroundColor = '#f8fafc';
        surveyCardsTitle.style.borderTop = '1px solid #e0e7ee';
        surveyCardsTitle.style.width = '842px';
        surveyCardsTitle.style.margin = '0';
        surveyCardsTitle.style.boxSizing = 'border-box';
        surveyCardsTitle.innerHTML = `
          <h3 style="margin: 0; font-size: 24px; color: #333; font-family: Arial, sans-serif; text-align: center; font-weight: 600;">
            Submitted AI Opportunities (${allSurveys.length})
          </h3>
        `;
        downloadContainer.appendChild(surveyCardsTitle);

        // Add survey cards in a 4-column grid layout (wider format allows more columns)
        const surveyCardsContainer = document.createElement('div');
        surveyCardsContainer.style.padding = '0 5px 15px 5px';
        surveyCardsContainer.style.backgroundColor = '#f8fafc';
        surveyCardsContainer.style.display = 'grid';
        surveyCardsContainer.style.gridTemplateColumns = 'repeat(4, 1fr)';
        surveyCardsContainer.style.gap = '8px';
        surveyCardsContainer.style.width = '842px';
        surveyCardsContainer.style.margin = '0';
        surveyCardsContainer.style.boxSizing = 'border-box';
        
        // Category colors mapping
        const categoryColors = {
          "Quick Wins": "#36b37e",
          "Strategic Challenges": "#ff5630",
          "Technical Experiments": "#0065ff",
          "Lower Priority": "#6b778c"
        };

        allSurveys.forEach((survey, index) => {
          // eslint-disable-next-line no-unused-vars
          const color = categoryColors[survey.category] || '#6b778c';
          const techScore = parseFloat(survey.y).toFixed(1);
          const roiScore = parseFloat(survey.x).toFixed(1);
          const strategicScore = parseFloat(survey.r / 3).toFixed(1);

          const surveyCard = document.createElement('div');
          // Match the CSS styling from .opportunity-card
          surveyCard.style.backgroundColor = 'white';
          surveyCard.style.border = '1px solid #eaedf2';
          surveyCard.style.borderRadius = '6px';
          surveyCard.style.fontFamily = 'Arial, sans-serif';
          surveyCard.style.boxShadow = '0 1px 4px rgba(0, 0, 0, 0.06)';
          surveyCard.style.overflow = 'hidden';
          surveyCard.style.display = 'flex';
          surveyCard.style.flexDirection = 'column';
          surveyCard.style.minHeight = '280px';
          surveyCard.style.width = '100%';
          surveyCard.style.boxSizing = 'border-box';
          
          surveyCard.innerHTML = `
            <div style="display: flex; flex-direction: column; height: 100%;">
              <!-- Card Title Section -->
              <div style="padding: 8px 8px 0;">
                <div style="font-size: 12px; font-weight: 600; color: #333; margin-bottom: 4px; line-height: 1.3; word-wrap: break-word;">
                  ${survey.opportunity_name || "AI Opportunity"}
                </div>
              </div>
              
              <!-- Card Description Section -->
              <div style="font-size: 10px; color: #555; line-height: 1.4; padding: 0 8px 6px; flex-grow: 1; word-wrap: break-word;">
                ${survey.opportunity_description || "No description available"}
              </div>

              <!-- Metrics Section (matches .opportunity-card-metrics) -->
              <div style="display: flex; justify-content: space-between; margin-top: auto; border-top: 1px solid #eee; padding: 6px 8px; background-color: #fafbfd;">
                <div style="text-align: center; flex: 1;">
                  <div style="font-size: 9px; text-transform: uppercase; font-weight: 500; color: #666; margin-bottom: 2px;">ROI</div>
                  <div style="font-size: 13px; font-weight: bold; color: #0065ff;">
                    ${roiScore >= 0 ? `+${roiScore}` : roiScore}
                  </div>
                </div>
                
                <div style="text-align: center; flex: 1;">
                  <div style="font-size: 9px; text-transform: uppercase; font-weight: 500; color: #666; margin-bottom: 2px;">TECH</div>
                  <div style="font-size: 13px; font-weight: bold; color: #36b37e;">
                    ${techScore >= 0 ? `+${techScore}` : techScore}
                  </div>
                </div>
                
                <div style="text-align: center; flex: 1;">
                  <div style="font-size: 9px; text-transform: uppercase; font-weight: 500; color: #666; margin-bottom: 2px;">STRATEGIC</div>
                  <div style="font-size: 13px; font-weight: bold; color: #ff5630;">
                    ${strategicScore}
                  </div>
                </div>
              </div>
            </div>
          `;
          
          surveyCardsContainer.appendChild(surveyCard);
        });

        downloadContainer.appendChild(surveyCardsContainer);
      }
    }
    
    // Add opportunity description section for single surveys and combined surveys
    if ((!isPortfolio && !companyDomain && chartData && chartData.categorized_data) || 
        (selectedSubmissions && selectedSubmissions.length > 0 && chartData && chartData.categorized_data)) {
      console.log('PDF: Attempting to add opportunity description');
      console.log('PDF: submissionData:', submissionData);
      console.log('PDF: chartData.categorized_data:', chartData.categorized_data);
      console.log('PDF: selectedSubmissions:', selectedSubmissions);
      
      // For combined charts, collect all descriptions
      if (selectedSubmissions && selectedSubmissions.length > 0) {
        // Add title section
        const surveyCardsTitle = document.createElement('div');
        surveyCardsTitle.style.padding = '15px 0 10px';
        surveyCardsTitle.style.backgroundColor = '#f8fafc';
        surveyCardsTitle.style.borderTop = '1px solid #e0e7ee';
        surveyCardsTitle.style.width = '595px';
        surveyCardsTitle.style.margin = '0';
        surveyCardsTitle.style.boxSizing = 'border-box';
        surveyCardsTitle.innerHTML = `
          <h3 style="margin: 0; font-size: 24px; color: #333; font-family: Arial, sans-serif; text-align: center; font-weight: 600;">
            Selected Opportunities (${selectedSubmissions.length})
          </h3>
        `;
        downloadContainer.appendChild(surveyCardsTitle);
        
        // Create 3-column grid container
        const cardsContainer = document.createElement('div');
        cardsContainer.style.padding = '0 5px 15px 5px';
        cardsContainer.style.backgroundColor = '#f8fafc';
        cardsContainer.style.display = 'grid';
        cardsContainer.style.gridTemplateColumns = 'repeat(3, 1fr)';
        cardsContainer.style.gap = '8px';
        cardsContainer.style.width = '595px';
        cardsContainer.style.margin = '0';
        cardsContainer.style.boxSizing = 'border-box';
        
        // Collect all opportunities from all categories
        const allOpportunities = [];
        Object.entries(chartData.categorized_data).forEach(([category, items]) => {
          items.forEach(item => {
            allOpportunities.push({ ...item, category });
          });
        });
        
        // Add each opportunity card
        allOpportunities.forEach((opportunity) => {
          const oppCard = document.createElement('div');
          oppCard.style.backgroundColor = 'white';
          oppCard.style.border = '1px solid #eaedf2';
          oppCard.style.borderRadius = '6px';
          oppCard.style.fontFamily = 'Arial, sans-serif';
          oppCard.style.boxShadow = '0 1px 4px rgba(0, 0, 0, 0.06)';
          oppCard.style.overflow = 'hidden';
          oppCard.style.display = 'flex';
          oppCard.style.flexDirection = 'column';
          oppCard.style.minHeight = '280px';
          oppCard.style.width = '100%';
          oppCard.style.boxSizing = 'border-box';
          
          const techScore = parseFloat(opportunity.y).toFixed(1);
          const roiScore = parseFloat(opportunity.x).toFixed(1);
          const strategicScore = parseFloat(opportunity.r / 3).toFixed(1);
          
          oppCard.innerHTML = `
            <div style="display: flex; flex-direction: column; height: 100%;">
              <!-- Card Title Section -->
              <div style="padding: 8px 8px 0;">
                <div style="font-size: 12px; font-weight: 600; color: #333; margin-bottom: 4px; line-height: 1.3; word-wrap: break-word;">
                  ${opportunity.opportunity_name || "AI Opportunity"}
                </div>
              </div>
              
              <!-- Card Description Section -->
              <div style="font-size: 10px; color: #555; line-height: 1.4; padding: 0 8px 6px; flex-grow: 1; word-wrap: break-word;">
                ${opportunity.opportunity_description || "No description available"}
              </div>

              <!-- Metrics Section -->
              <div style="display: flex; justify-content: space-between; margin-top: auto; border-top: 1px solid #eee; padding: 6px 8px; background-color: #fafbfd;">
                <div style="text-align: center; flex: 1;">
                  <div style="font-size: 9px; text-transform: uppercase; font-weight: 500; color: #666; margin-bottom: 2px;">ROI</div>
                  <div style="font-size: 13px; font-weight: bold; color: #0065ff;">
                    ${roiScore >= 0 ? `+${roiScore}` : roiScore}
                  </div>
                </div>
                
                <div style="text-align: center; flex: 1;">
                  <div style="font-size: 9px; text-transform: uppercase; font-weight: 500; color: #666; margin-bottom: 2px;">TECH</div>
                  <div style="font-size: 13px; font-weight: bold; color: #36b37e;">
                    ${techScore >= 0 ? `+${techScore}` : techScore}
                  </div>
                </div>
                
                <div style="text-align: center; flex: 1;">
                  <div style="font-size: 9px; text-transform: uppercase; font-weight: 500; color: #666; margin-bottom: 2px;">STRATEGIC</div>
                  <div style="font-size: 13px; font-weight: bold; color: #ff5630;">
                    ${strategicScore}
                  </div>
                </div>
              </div>
            </div>
          `;
          
          cardsContainer.appendChild(oppCard);
        });
        
        downloadContainer.appendChild(cardsContainer);
      } else {
        // Single survey - use existing logic
        // First try to use submissionData if available (passed from AdminDashboard)
        let description = submissionData?.opportunity_description;
        
        // If not available in submissionData, try to get from chartData
        if (!description) {
          let opportunityDetails = null;
          for (const category in chartData.categorized_data) {
            const items = chartData.categorized_data[category];
            console.log(`PDF: Category ${category}, items count:`, items?.length);
            if (items && items.length > 0) {
              opportunityDetails = items[0];
              console.log('PDF: Found opportunity details:', opportunityDetails);
              break;
            }
          }
          description = opportunityDetails?.opportunity_description;
        }
        
        if (description) {
          console.log('PDF: Adding description section with:', description);
          
          const descriptionSection = document.createElement('div');
          descriptionSection.style.padding = '25px 20px';
          descriptionSection.style.backgroundColor = '#ffffff';
          descriptionSection.style.borderTop = '1px solid #e0e7ee';
          descriptionSection.style.width = '842px';
          descriptionSection.style.margin = '0';
          descriptionSection.style.boxSizing = 'border-box';
          descriptionSection.innerHTML = `
            <div style="border-left: 4px solid #0066cc; padding-left: 20px; background-color: #f7f9fc; padding: 20px; padding-left: 20px; border-radius: 2px;">
              <h3 style="margin: 0 0 15px 0; font-size: 20px; color: #0066cc; font-family: Arial, sans-serif; font-weight: 600;">
                ${submissionData?.opportunity_name || getOpportunityName()}
              </h3>
              <div style="font-size: 15px; color: #4a5568; line-height: 1.8; font-family: Arial, sans-serif; text-align: justify;">
                ${description}
              </div>
            </div>
          `;
          downloadContainer.appendChild(descriptionSection);
        } else {
          console.log('PDF: No opportunity description found in submissionData or chartData');
        }
      }
    } else {
      console.log('PDF: Condition not met for description. isPortfolio:', isPortfolio, 'companyDomain:', companyDomain, 'hasChartData:', !!chartData, 'selectedSubmissions:', selectedSubmissions);
    }
    
    // Add scores section if it's a single assessment (but not a company chart or combined chart)
    const scores = getScores();
    if (scores && !isPortfolio && !companyDomain && !(selectedSubmissions && selectedSubmissions.length > 0)) {
      const scoresDiv = document.createElement('div');
      scoresDiv.style.display = 'flex';
      scoresDiv.style.gap = '10px';
      scoresDiv.style.padding = '15px 5px';
      scoresDiv.style.width = '842px';
      scoresDiv.style.margin = '0';
      scoresDiv.style.boxSizing = 'border-box';
      scoresDiv.style.backgroundColor = '#f5f8fa';
      scoresDiv.style.borderTop = '1px solid #e0e7ee';
      
      // Tech Feasibility Card
      const techCard = document.createElement('div');
      techCard.style.flex = '1';
      techCard.style.backgroundColor = 'white';
      techCard.style.border = '1px solid #dee2e6';
      techCard.style.borderRadius = '6px';
      techCard.style.padding = '10px';
      techCard.style.fontFamily = 'Arial, sans-serif';
      techCard.innerHTML = `
        <h3 style="font-size: 13px; margin-top: 0; margin-bottom: 8px; color: #555; border-bottom: 2px solid #36b37e; padding-bottom: 6px; font-family: Arial, sans-serif;">
          Tech Feasibility
        </h3>
        <div style="font-size: 22px; font-weight: bold; margin: 10px 0; text-align: center; color: #36b37e; font-family: Arial, sans-serif;">
          +${scores.tech}
        </div>
        <div style="font-size: 11px; line-height: 1.4; font-family: Arial, sans-serif;">
          <ul style="padding-left: 15px; margin: 6px 0;">
            <li style="margin-bottom: 4px;">✓ Predictable patterns</li>
            <li style="margin-bottom: 4px;">✓ Cloud services acceptable</li>
            <li style="margin-bottom: 4px;">✓ APIs available</li>
          </ul>
        </div>
      `;
      scoresDiv.appendChild(techCard);
      
      // ROI Potential Card
      const roiCard = document.createElement('div');
      roiCard.style.flex = '1';
      roiCard.style.backgroundColor = 'white';
      roiCard.style.border = '1px solid #dee2e6';
      roiCard.style.borderRadius = '6px';
      roiCard.style.padding = '10px';
      roiCard.style.fontFamily = 'Arial, sans-serif';
      roiCard.innerHTML = `
        <h3 style="font-size: 13px; margin-top: 0; margin-bottom: 8px; color: #555; border-bottom: 2px solid #0065ff; padding-bottom: 6px; font-family: Arial, sans-serif;">
          ROI Potential
        </h3>
        <div style="font-size: 22px; font-weight: bold; margin: 10px 0; text-align: center; color: #0065ff; font-family: Arial, sans-serif;">
          +${scores.roi}
        </div>
        <div style="font-size: 11px; line-height: 1.4; font-family: Arial, sans-serif;">
          <ul style="padding-left: 15px; margin: 6px 0;">
            <li style="margin-bottom: 4px;">✓ High transaction volume</li>
            <li style="margin-bottom: 4px;">✓ 25% time savings</li>
            <li style="margin-bottom: 4px;">✓ Risk reduction</li>
          </ul>
        </div>
      `;
      scoresDiv.appendChild(roiCard);
      
      // Strategic Value Card
      const strategicCard = document.createElement('div');
      strategicCard.style.flex = '1';
      strategicCard.style.backgroundColor = 'white';
      strategicCard.style.border = '1px solid #dee2e6';
      strategicCard.style.borderRadius = '6px';
      strategicCard.style.padding = '10px';
      strategicCard.style.fontFamily = 'Arial, sans-serif';
      strategicCard.innerHTML = `
        <h3 style="font-size: 13px; margin-top: 0; margin-bottom: 8px; color: #555; border-bottom: 2px solid #ff5630; padding-bottom: 6px; font-family: Arial, sans-serif;">
          Strategic Value
        </h3>
        <div style="font-size: 22px; font-weight: bold; margin: 10px 0; text-align: center; color: #ff5630; font-family: Arial, sans-serif;">
          ${scores.strategic}
        </div>
        <div style="font-size: 11px; line-height: 1.4; font-family: Arial, sans-serif;">
          <ul style="padding-left: 15px; margin: 6px 0;">
            <li style="margin-bottom: 4px;">✓ Business alignment</li>
            <li style="margin-bottom: 4px;">✓ Efficiency gains</li>
            <li style="margin-bottom: 4px;">✓ Operational excellence</li>
          </ul>
        </div>
      `;
      scoresDiv.appendChild(strategicCard);
      
      downloadContainer.appendChild(scoresDiv);
    }
    
    // Recreate the chart on the download canvas
    const recreateChart = () => {
      if (!chartData) return;
      
      const ctx = newCanvas.getContext('2d');
      
      // Create datasets from categorized data - same as the original chart
      const datasets = [];
      
      // Same color mapping as the original chart
      const updatedColors = {
        "Quick Wins": "#36b37e", // Green
        "Strategic Challenges": "#ff5630", // Red-Orange
        "Technical Experiments": "#0065ff", // Blue
        "Lower Priority": "#6b778c" // Gray
      };
      
      Object.entries(chartData.categorized_data || {}).forEach(([category, items]) => {
        if (items.length === 0) return;
        
        datasets.push({
          label: category,
          data: items.map(item => ({
            x: item.x,
            y: item.y,
            r: item.r || 5, // Default radius if missing
            ...item // Pass through all properties for tooltip access
          })),
          backgroundColor: items.map(item => {
            const color = updatedColors[category] || chartData.colors[category];
            return item.highlighted 
              ? `${color}E6` // 90% opacity
              : `${color}B3`; // 70% opacity
          }),
          borderColor: items.map(() => updatedColors[category] || chartData.colors[category]),
          borderWidth: items.map(item => item.highlighted ? 2 : 1),
          hoverBorderWidth: 2,
          hoverBackgroundColor: items.map(item => {
            const color = updatedColors[category] || chartData.colors[category];
            return `${color}`;
          }),
          pointHoverRadius: 7 // Slightly larger on hover
        });
      });

      // Create the new chart with identical configuration
      const downloadChartInstance = new Chart(ctx, {
        type: 'bubble',
        data: { datasets },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          animation: false, // Disable animation for download
          layout: {
            padding: {
              top: 15,
              right: 15,
              bottom: 15,
              left: 15
            }
          },
          scales: {
            x: {
              title: {
                display: true,
                text: 'ROI Potential',
                font: { weight: 'bold', family: 'Arial', size: 15 },
                padding: { top: 15 },
                color: '#333'
              },
              min: -10,
              max: 10,
              grid: {
                color: 'rgba(200, 200, 200, 0.15)',
                lineWidth: 1,
                drawBorder: false
              },
              border: {
                display: true,
                color: 'rgba(0, 0, 0, 0.9)',
                width: 3
              },
              ticks: {
                color: '#444',
                font: {
                  family: 'Arial',
                  size: 11,
                  weight: '500'
                },
                maxRotation: 0,
                padding: 8,
                autoSkip: false,
                callback: function(value) {
                  if (value === -8) return 'Very Low';
                  if (value === 0) return 'Neutral';
                  if (value === 8) return 'Very High';
                  if (value === -10 || value === 10) return '';
                  if (value % 2 === 0) return value;
                  return '';
                }
              },
              position: 'center',
              beginAtZero: false
            },
            y: {
              title: {
                display: true,
                text: 'Tech Feasibility',
                font: { weight: 'bold', family: 'Arial', size: 15 },
                padding: { bottom: 15 },
                color: '#333'
              },
              min: -10,
              max: 10,
              grid: {
                color: 'rgba(200, 200, 200, 0.15)',
                lineWidth: 1,
                drawBorder: false
              },
              border: {
                display: true,
                color: 'rgba(0, 0, 0, 0.9)',
                width: 3
              },
              ticks: {
                color: '#444',
                font: {
                  family: 'Arial',
                  size: 11,
                  weight: '500'
                },
                padding: 8,
                autoSkip: false,
                callback: function(value) {
                  if (value === -8) return 'Very Low';
                  if (value === 0) return 'Neutral';
                  if (value === 8) return 'Very High';
                  if (value === -10 || value === 10) return '';
                  if (value % 2 === 0) return value;
                  return '';
                }
              },
              position: 'center',
              beginAtZero: false
            }
          },
          plugins: {
            title: {
              display: true,
              text: 'Strategic Assessment Matrix',
              font: { 
                size: 18, 
                weight: 'bold', 
                family: 'Arial'
              },
              color: '#333',
              padding: { bottom: 5 }
            },
            subtitle: {
              display: true,
              text: 'TD&R, Internal Audit & Compliance Functions',
              font: {
                size: 14,
                family: 'Arial',
                weight: 'normal'
              },
              color: '#666',
              padding: { bottom: 15 }
            },
            legend: {
              display: false // Hide default legend as we'll create a custom one
            }
          }
        },
        plugins: [{
          id: 'downloadQuadrantLines',
          afterDraw: (chart) => {
            if (!chart || !chart.ctx) {
              return; // Guard against null chart or context
            }
            const ctx = chart.ctx;
            
            // Save the original state
            ctx.save();
            
            // Draw vertical & horizontal center lines
            ctx.beginPath();
            ctx.strokeStyle = 'rgba(0, 0, 0, 0.75)';
            ctx.lineWidth = 2.5;
            
            // Vertical line at x=0
            const xZero = chart.scales.x.getPixelForValue(0);
            ctx.moveTo(xZero, chart.scales.y.getPixelForValue(10));
            ctx.lineTo(xZero, chart.scales.y.getPixelForValue(-10));
            
            // Horizontal line at y=0
            const yZero = chart.scales.y.getPixelForValue(0);
            ctx.moveTo(chart.scales.x.getPixelForValue(-10), yZero);
            ctx.lineTo(chart.scales.x.getPixelForValue(10), yZero);
            
            ctx.stroke();
            ctx.restore();
          }
        }]
      });
      
      // Return the chart instance for cleanup
      return downloadChartInstance;
    };
    
    // Create the chart for download
    const downloadChartInstance = recreateChart();
    
    // Allow time for the chart to render
    setTimeout(() => {
      // Use html2canvas to capture the temporary container
      html2canvas(downloadContainer, { 
        backgroundColor: '#ffffff',
        scale: 2, // Higher resolution
        logging: false,
        width: 842, // Match A4 landscape container width
        height: downloadContainer.scrollHeight, // Capture full height
        windowWidth: 842, // Ensure proper rendering width
        x: 0, // Start from left edge
        y: 0, // Start from top edge
        scrollX: 0,
        scrollY: 0,
        useCORS: true, // Handle cross-origin images if any
        allowTaint: true
      }).then(canvas => {
        // Clean up
        if (downloadChartInstance) {
          downloadChartInstance.destroy();
        }
        document.body.removeChild(downloadContainer);
        
        // Convert canvas to PDF
        const imgData = canvas.toDataURL('image/png', 1.0);
        const pdf = new jsPDF({
          orientation: 'portrait',
          unit: 'px',
          format: [canvas.width / 2, canvas.height / 2], // Use exact canvas dimensions (divided by scale)
          compress: true
        });
        
        // Get PDF dimensions in pixels
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = pdf.internal.pageSize.getHeight();
        
        // Add image to fill entire page - edge to edge, no whitespace
        pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight, undefined, 'FAST');
        
        // Save the PDF
        const filename = isPortfolio 
          ? `ai-opportunity-portfolio-${new Date().toISOString().split('T')[0]}.pdf`
          : companyDomain 
            ? `ai-opportunity-company-${capitalizeFirstLetter(companyDomain)}-${new Date().toISOString().split('T')[0]}.pdf`
            : (selectedSubmissions && selectedSubmissions.length > 0)
              ? `ai-opportunity-combined-${new Date().toISOString().split('T')[0]}.pdf`
              : `ai-opportunity-assessment-single-${new Date().toISOString().split('T')[0]}.pdf`;
        pdf.save(filename);
        
      }).catch(err => {
        console.error("Error generating chart PDF:", err);
        if (downloadChartInstance) {
          downloadChartInstance.destroy();
        }
        document.body.removeChild(downloadContainer);
      });
    }, 500); // Short delay to ensure chart rendering
  };

  // Function to download the chart as HTML
  const downloadChartAsHTML = async () => {
    if (!chartContainerRef.current || !chartData) return;
    
    // Convert logo to base64 for embedding in HTML
    let logoBase64 = '';
    try {
      const response = await fetch(bmailogo);
      const blob = await response.blob();
      logoBase64 = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error('Error loading logo for HTML export:', error);
    }
    
    // Get the same data as the PDF
    const opportunityName = getOpportunityName();
    const scores = getScores();
    
    // Color mapping - same as the actual chart
    const updatedColors = {
      "Quick Wins": "#36b37e",
      "Strategic Challenges": "#ff5630",
      "Technical Experiments": "#0065ff",
      "Lower Priority": "#6b778c"
    };
    
    // Build datasets exactly like the rendered chart
    const datasetsJson = [];
    Object.entries(chartData.categorized_data || {}).forEach(([category, items]) => {
      if (items.length === 0) return;
      
      datasetsJson.push({
        label: category,
        data: items.map(item => ({
          x: item.x,
          y: item.y,
          r: item.r || 5,
          opportunity_name: item.opportunity_name,
          strategic_category: item.strategic_category
        })),
        backgroundColor: items.map(item => {
          const color = updatedColors[category];
          return item.highlighted ? `${color}E6` : `${color}B3`;
        }),
        borderColor: updatedColors[category],
        borderWidth: items.map(item => item.highlighted ? 2 : 1)
      });
    });
    
    let htmlContent = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>AI Opportunity - ${opportunityName}</title>
  <style>
    body {
      font-family: Arial, sans-serif;
      margin: 0;
      padding: 20px;
      background-color: #f5f5f5;
    }
    .container {
      max-width: 1200px;
      margin: 0 auto;
      background-color: white;
      border-radius: 8px;
      box-shadow: 0 2px 8px rgba(0,0,0,0.1);
      overflow: hidden;
    }
    .header {
      background: #000000;
      color: white;
      padding: 30px;
      text-align: center;
    }
    .header h1 {
      margin: 0 0 10px 0;
      font-size: 28px;
      font-weight: 600;
      letter-spacing: 0.5px;
    }
    .header .subtitle {
      font-size: 17px;
      opacity: 0.9;
      margin-top: 8px;
    }
    .header .legend {
      font-size: 13px;
      line-height: 1.6;
      opacity: 0.85;
      margin-top: 15px;
    }
    .chart-section {
      padding: 30px;
      position: relative;
    }
    #assessmentChart {
      max-width: 100%;
      height: 500px;
    }
    .scores-section {
      display: flex;
      gap: 15px;
      padding: 15px;
      background-color: #f5f8fa;
      border-top: 1px solid #e0e7ee;
    }
    .score-card {
      flex: 1;
      background-color: white;
      border: 1px solid #dee2e6;
      border-radius: 8px;
      padding: 15px;
    }
    .score-card h3 {
      font-size: 16px;
      margin-top: 0;
      margin-bottom: 12px;
      padding-bottom: 8px;
      color: #555;
    }
    .score-card.tech h3 { border-bottom: 2px solid #36b37e; }
    .score-card.roi h3 { border-bottom: 2px solid #0065ff; }
    .score-card.strategic h3 { border-bottom: 2px solid #ff5630; }
    .score-value {
      font-size: 24px;
      font-weight: bold;
      text-align: center;
      margin: 15px 0;
    }
    .score-card.tech .score-value { color: #36b37e; }
    .score-card.roi .score-value { color: #0065ff; }
    .score-card.strategic .score-value { color: #ff5630; }
    .score-details {
      font-size: 13px;
      line-height: 1.4;
    }
    .score-details ul {
      padding-left: 18px;
      margin: 8px 0;
    }
    .score-details li {
      margin-bottom: 5px;
    }
    .footer {
      padding: 20px 30px;
      text-align: center;
      color: #666;
      font-size: 14px;
      border-top: 1px solid #eee;
    }
    .survey-cards-section {
      padding: 30px 0;
      background-color: #f8fafc;
      border-top: 1px solid #e0e7ee;
    }
    .survey-cards-title {
      text-align: center;
      margin-bottom: 20px;
      padding: 0 30px;
    }
    .survey-cards-title h3 {
      margin: 0;
      font-size: 20px;
      color: #333;
      font-weight: 600;
    }
    .survey-cards-grid {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 15px;
      padding: 0 30px;
      width: 100%;
      box-sizing: border-box;
    }
    .opportunity-card {
      background-color: white;
      border: 1px solid #eaedf2;
      border-radius: 8px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
      overflow: hidden;
      display: flex;
      flex-direction: column;
      height: 100%;
      min-height: 220px;
      box-sizing: border-box;
    }
    .opportunity-card-content {
      display: flex;
      flex-direction: column;
      height: 100%;
    }
    .opportunity-card-title {
      font-size: 16px;
      font-weight: 600;
      color: #333;
      margin-bottom: 8px;
      line-height: 1.3;
      padding: 16px 16px 0;
    }
    .opportunity-card-description {
      font-size: 13px;
      color: #555;
      line-height: 1.4;
      padding: 0 16px 16px;
      flex-grow: 1;
    }
    .opportunity-card-metrics {
      display: flex;
      justify-content: space-between;
      margin-top: auto;
      border-top: 1px solid #eee;
      padding: 12px 16px;
      background-color: #fafbfd;
    }
    .metrics-column {
      text-align: center;
      flex: 1;
    }
    .metrics-label {
      font-size: 12px;
      text-transform: uppercase;
      font-weight: 500;
      color: #666;
      margin-bottom: 4px;
    }
    .metrics-value {
      font-size: 18px;
      font-weight: bold;
    }
    .metrics-value.roi { color: #0065ff; }
    .metrics-value.tech { color: #36b37e; }
    .metrics-value.strategic { color: #ff5630; }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      ${logoBase64 ? `<img src="${logoBase64}" alt="Bytemethod AI Logo" style="width: 300px; height: auto; margin-bottom: 20px; display: inline-block;" />` : ''}
      <h1>${opportunityName}</h1>
      <div class="subtitle">Opportunity Survey Analysis</div>
      <div class="legend">
        • X-axis: ROI Potential (-10 to +10)<br>
        • Y-axis: Tech Feasibility (-10 to +10)<br>
        • Bubble Size: Strategic Value (1-10)
      </div>
    </div>
    
    <div class="chart-section">
      <canvas id="assessmentChart"></canvas>
    </div>
    
    <script src="https://cdn.jsdelivr.net/npm/chart.js@4.4.0/dist/chart.umd.min.js"></script>
    <script>
      const datasets = ${JSON.stringify(datasetsJson)};
      
      const ctx = document.getElementById('assessmentChart').getContext('2d');
      const chart = new Chart(ctx, {
        type: 'bubble',
        data: { datasets: datasets },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          layout: {
            padding: { top: 25, right: 25, bottom: 25, left: 25 }
          },
          scales: {
            x: {
              title: {
                display: true,
                text: 'ROI Potential',
                font: { weight: 'bold', family: 'Arial', size: 15 },
                padding: { top: 15 },
                color: '#333'
              },
              min: -10,
              max: 10,
              grid: {
                color: 'rgba(200, 200, 200, 0.15)',
                lineWidth: 1,
                drawBorder: false
              },
              border: {
                display: true,
                color: 'rgba(0, 0, 0, 0.9)',
                width: 3
              },
              ticks: {
                color: '#444',
                font: { family: 'Arial', size: 11, weight: '500' },
                maxRotation: 0,
                padding: 8,
                autoSkip: false,
                callback: function(value) {
                  if (value === -8) return 'Very Low';
                  if (value === 0) return 'Neutral';
                  if (value === 8) return 'Very High';
                  if (value === -10 || value === 10) return '';
                  if (value % 2 === 0) return value;
                  return '';
                }
              },
              position: 'center'
            },
            y: {
              title: {
                display: true,
                text: 'Tech Feasibility',
                font: { weight: 'bold', family: 'Arial', size: 15 },
                padding: { bottom: 15 },
                color: '#333'
              },
              min: -10,
              max: 10,
              grid: {
                color: 'rgba(200, 200, 200, 0.15)',
                lineWidth: 1,
                drawBorder: false
              },
              border: {
                display: true,
                color: 'rgba(0, 0, 0, 0.9)',
                width: 3
              },
              ticks: {
                color: '#444',
                font: { family: 'Arial', size: 11, weight: '500' },
                padding: 8,
                autoSkip: false,
                callback: function(value) {
                  if (value === -8) return 'Very Low';
                  if (value === 0) return 'Neutral';
                  if (value === 8) return 'Very High';
                  if (value === -10 || value === 10) return '';
                  if (value % 2 === 0) return value;
                  return '';
                }
              },
              position: 'center'
            }
          },
          plugins: {
            title: {
              display: true,
              text: 'Strategic Assessment Matrix',
              font: { size: 18, weight: 'bold', family: 'Arial' },
              color: '#333',
              padding: { bottom: 5 }
            },
            subtitle: {
              display: true,
              text: 'TD&R, Internal Audit & Compliance Functions',
              font: { size: 14, family: 'Arial', weight: 'normal' },
              color: '#666',
              padding: { bottom: 15 }
            },
            legend: {
              display: false
            },
            tooltip: {
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              titleFont: { size: 14, weight: 'bold', family: 'Arial' },
              bodyFont: { size: 13, family: 'Arial' },
              titleColor: '#333',
              bodyColor: '#555',
              displayColors: false,
              padding: 12,
              cornerRadius: 4,
              borderColor: 'rgba(0, 0, 0, 0.1)',
              borderWidth: 1,
              callbacks: {
                title: function(items) {
                  return items[0].raw.opportunity_name || 'Unnamed Opportunity';
                },
                label: function(context) {
                  const item = context.raw;
                  const formatValue = (v) => {
                    const num = Number(v);
                    const fixed = num.toFixed(1);
                    return num > 0 ? '+' + fixed : fixed;
                  };
                  return [
                    'Category: ' + (item.strategic_category || 'Not categorized'),
                    'ROI Potential: ' + formatValue(item.x),
                    'Tech Feasibility: ' + formatValue(item.y),
                    'Strategic Value: ' + formatValue(item.r / 3)
                  ];
                }
              }
            }
          }
        },
        plugins: [{
          id: 'quadrantLines',
          afterDraw: function(chart) {
            const ctx = chart.ctx;
            ctx.save();
            ctx.beginPath();
            ctx.strokeStyle = 'rgba(0, 0, 0, 0.75)';
            ctx.lineWidth = 2.5;
            const xZero = chart.scales.x.getPixelForValue(0);
            ctx.moveTo(xZero, chart.scales.y.getPixelForValue(10));
            ctx.lineTo(xZero, chart.scales.y.getPixelForValue(-10));
            const yZero = chart.scales.y.getPixelForValue(0);
            ctx.moveTo(chart.scales.x.getPixelForValue(-10), yZero);
            ctx.lineTo(chart.scales.x.getPixelForValue(10), yZero);
            ctx.stroke();
            ctx.restore();
          }
        }]
      });
    </script>`;
    
    // Add survey cards section for portfolio and company charts
    if ((isPortfolio || companyDomain) && chartData && chartData.categorized_data) {
      // Collect all surveys across all categories
      const allSurveys = [];
      Object.entries(chartData.categorized_data).forEach(([category, items]) => {
        items.forEach(item => {
          allSurveys.push({
            ...item,
            category
          });
        });
      });

      if (allSurveys.length > 0) {
        htmlContent += `
    <div class="survey-cards-section">
      <div class="survey-cards-title">
        <h3>Submitted AI Opportunities (${allSurveys.length})</h3>
      </div>
      <div class="survey-cards-grid">`;
        
        allSurveys.forEach((survey, index) => {
          const techScore = parseFloat(survey.y).toFixed(1);
          const roiScore = parseFloat(survey.x).toFixed(1);
          const strategicScore = parseFloat(survey.r / 3).toFixed(1);
          
          htmlContent += `
        <div class="opportunity-card">
          <div class="opportunity-card-content">
            <div class="opportunity-card-title">
              ${survey.opportunity_name || "AI Opportunity"}
            </div>
            
            <div class="opportunity-card-description">
              ${survey.opportunity_description || "No description available"}
            </div>

            <div class="opportunity-card-metrics">
              <div class="metrics-column">
                <div class="metrics-label">ROI</div>
                <div class="metrics-value roi">${roiScore >= 0 ? `+${roiScore}` : roiScore}</div>
              </div>
              
              <div class="metrics-column">
                <div class="metrics-label">TECH</div>
                <div class="metrics-value tech">${techScore >= 0 ? `+${techScore}` : techScore}</div>
              </div>
              
              <div class="metrics-column">
                <div class="metrics-label">STRATEGIC</div>
                <div class="metrics-value strategic">${strategicScore}</div>
              </div>
            </div>
          </div>
        </div>`;
        });
        
        htmlContent += `
      </div>
    </div>`;
      }
    }
    
    // Add opportunity description section for single surveys and combined surveys
    if ((!isPortfolio && !companyDomain && chartData && chartData.categorized_data) || 
        (selectedSubmissions && selectedSubmissions.length > 0 && chartData && chartData.categorized_data)) {
      console.log('HTML: Attempting to add opportunity description');
      console.log('HTML: submissionData:', submissionData);
      console.log('HTML: chartData.categorized_data:', chartData.categorized_data);
      console.log('HTML: selectedSubmissions:', selectedSubmissions);
      
      // For combined charts, add all descriptions
      if (selectedSubmissions && selectedSubmissions.length > 0) {
        htmlContent += `
    <div class="survey-cards-section">
      <div class="survey-cards-title">
        <h3>Selected Opportunities (${selectedSubmissions.length})</h3>
      </div>
      <div class="survey-cards-grid">`;
        
        // Collect all opportunities from all categories
        const allOpportunities = [];
        Object.entries(chartData.categorized_data).forEach(([category, items]) => {
          items.forEach(item => {
            allOpportunities.push({ ...item, category });
          });
        });
        
        // Add each opportunity card
        allOpportunities.forEach((opportunity) => {
          const techScore = parseFloat(opportunity.y).toFixed(1);
          const roiScore = parseFloat(opportunity.x).toFixed(1);
          const strategicScore = parseFloat(opportunity.r / 3).toFixed(1);
          
          htmlContent += `
        <div class="opportunity-card">
          <div class="opportunity-card-content">
            <div class="opportunity-card-title">
              ${opportunity.opportunity_name || "AI Opportunity"}
            </div>
            
            <div class="opportunity-card-description">
              ${opportunity.opportunity_description || "No description available"}
            </div>

            <div class="opportunity-card-metrics">
              <div class="metrics-column">
                <div class="metrics-label">ROI</div>
                <div class="metrics-value roi">${roiScore >= 0 ? `+${roiScore}` : roiScore}</div>
              </div>
              
              <div class="metrics-column">
                <div class="metrics-label">Tech</div>
                <div class="metrics-value tech">${techScore >= 0 ? `+${techScore}` : techScore}</div>
              </div>
              
              <div class="metrics-column">
                <div class="metrics-label">Strategic</div>
                <div class="metrics-value strategic">${strategicScore}</div>
              </div>
            </div>
          </div>
        </div>`;
        });
        
        htmlContent += `
      </div>
    </div>`;
      } else {
        // Single survey - use existing logic
        // First try to use submissionData if available (passed from AdminDashboard)
        let description = submissionData?.opportunity_description;
        
        // If not available in submissionData, try to get from chartData
        if (!description) {
          let opportunityDetails = null;
          for (const category in chartData.categorized_data) {
            const items = chartData.categorized_data[category];
            console.log(`HTML: Category ${category}, items count:`, items?.length);
            if (items && items.length > 0) {
              opportunityDetails = items[0];
              console.log('HTML: Found opportunity details:', opportunityDetails);
              break;
            }
          }
          description = opportunityDetails?.opportunity_description;
        }
        
        if (description) {
          console.log('HTML: Adding description section with:', description);
          
          htmlContent += `
    <div style="padding: 20px; background-color: #f8fafc; border-top: 1px solid #e0e7ee;">
      <h3 style="margin: 0 0 12px 0; font-size: 18px; color: #333; font-family: Arial, sans-serif; font-weight: 600;">
        Opportunity Description
      </h3>
      <div style="font-size: 14px; color: #555; line-height: 1.6; font-family: Arial, sans-serif;">
        ${description}
      </div>
    </div>`;
        } else {
          console.log('HTML: No opportunity description found in submissionData or chartData');
        }
      }
    } else {
      console.log('HTML: Condition not met for description. isPortfolio:', isPortfolio, 'companyDomain:', companyDomain, 'hasChartData:', !!chartData, 'selectedSubmissions:', selectedSubmissions);
    }
    
    if (scores && !isPortfolio && !companyDomain && !(selectedSubmissions && selectedSubmissions.length > 0)) {
      htmlContent += `
    <div class="scores-section">
      <div class="score-card tech">
        <h3>Tech Feasibility Assessment</h3>
        <div class="score-value">+${scores.tech}</div>
        <div class="score-details">
          <ul>
            <li>✓ Predictable patterns in process flow</li>
            <li>✓ Cloud services acceptable for implementation</li>
            <li>✓ APIs available for required systems</li>
            <li>✓ Well-documented requirements</li>
          </ul>
        </div>
      </div>
      
      <div class="score-card roi">
        <h3>ROI Potential</h3>
        <div class="score-value">+${scores.roi}</div>
        <div class="score-details">
          <ul>
            <li>✓ High daily transaction volume</li>
            <li>✓ 25% time savings expected</li>
            <li>✓ Moderate change management needed</li>
            <li>✓ Significant risk reduction potential</li>
          </ul>
        </div>
      </div>
      
      <div class="score-card strategic">
        <h3>Strategic Value</h3>
        <div class="score-value">${scores.strategic}</div>
        <div class="score-details">
          <ul>
            <li>✓ Direct alignment with business objectives</li>
            <li>✓ Significant efficiency improvements</li>
            <li>✓ Core operational excellence</li>
          </ul>
        </div>
      </div>
    </div>`;
    }
    
    htmlContent += `
    <div class="footer">
      <p>AI Opportunity Report | Generated on ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
    </div>
  </div>
</body>
</html>`;
    
    // Create a Blob from the HTML content
    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    
    // Create download link
    const link = document.createElement('a');
    const htmlFilename = isPortfolio 
      ? 'portfolio' 
      : (selectedSubmissions && selectedSubmissions.length > 0) 
        ? 'combined' 
        : 'single';
    link.download = `ai-opportunity-assessment-${htmlFilename}-${new Date().toISOString().split('T')[0]}.html`;
    link.href = url;
    link.click();
    
    // Clean up
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="flex flex-col justify-center items-center h-64 bg-gray-50 rounded-lg">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mb-4"></div>
        <div className="text-gray-600 text-sm font-medium">Loading chart data...</div>
        <div className="text-gray-400 text-xs mt-1">
          {selectedSubmissions && selectedSubmissions.length > 0 
            ? `Loading data for ${selectedSubmissions.length} selected surveys` 
            : isPortfolio 
              ? 'Fetching portfolio data' 
              : companyDomain 
                ? `Loading ${capitalizeFirstLetter(companyDomain)} charts` 
                : 'Preparing visualization'}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col justify-center items-center h-64 bg-red-50 rounded-lg border border-red-200">
        <div className="text-red-600 text-sm font-medium mb-2">Failed to load chart</div>
        <div className="text-red-500 text-xs mb-4">{error}</div>
        <button 
          onClick={() => window.location.reload()} 
          className="px-4 py-2 bg-red-600 text-white text-xs rounded hover:bg-red-700 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  // Get scores for the first data point if available (for single assessment view)
  const getScores = () => {
    if (!chartData || isPortfolio) return null;
    
    // Find the first data point from any category
    for (const category in chartData.categorized_data) {
      const items = chartData.categorized_data[category];
      if (items && items.length > 0) {
        return {
          tech: items[0].y.toFixed(1),
          roi: items[0].x.toFixed(1),
          strategic: (items[0].r / 3).toFixed(1)
        };
      }
    }
    return null;
  };
  
  // Get the opportunity name from the chart data
  const getOpportunityName = () => {
    if (selectedSubmissions && selectedSubmissions.length > 0) return "Combined Chart";
    if (isPortfolio) return "AI Opportunity";
    if (companyDomain) return capitalizeFirstLetter(companyDomain);
    if (!chartData) return "AI Opportunity";
    // Find the opportunity name from the first data point in any category
    for (const category in chartData.categorized_data) {
      const items = chartData.categorized_data[category];
      if (items && items.length > 0 && items[0].opportunity_name) {
        return items[0].opportunity_name;
      }
    }
    return "AI Opportunity"; // Default fallback
  };

  const scores = getScores();

  // Function to generate survey cards for portfolio and company-based charts
  const renderSurveyCards = () => {
    // Render cards for portfolio, company, or combined charts (selectedSubmissions)
    const shouldRenderCards = isPortfolio || companyDomain || (selectedSubmissions && selectedSubmissions.length > 0);
    
    if (!shouldRenderCards) {
      return null;
    }
    
    if (!chartData || !chartData.categorized_data) {
      return null;
    }

    // Collect all surveys across all categories
    const allSurveys = [];
    Object.entries(chartData.categorized_data).forEach(([category, items]) => {
      items.forEach(item => {
        allSurveys.push({
          ...item,
          category
        });
      });
    });

    // If there are no surveys, return null
    if (allSurveys.length === 0) {
      return null;
    }

    // Helper function to get truncated description (first two lines)
    const getTruncatedDescription = (text) => {
      if (!text) return '';
      const words = text.split(' ');
      // Approximate 2 lines based on word count (~8-10 words per line)
      const truncateAt = 16;
      if (words.length <= truncateAt) return text;
      return words.slice(0, truncateAt).join(' ') + '...';
    };

    // Toggle description expansion
    const toggleDescription = (index) => {
      setExpandedDescriptions(prev => ({
        ...prev,
        [index]: !prev[index]
      }));
    };

    return (
      <div className="survey-cards-container">
        <div className="survey-cards-grid">
          {allSurveys.map((survey, index) => {
            const categoryColors = {
              "Quick Wins": "#36b37e",
              "Strategic Challenges": "#ff5630",
              "Technical Experiments": "#0065ff",
              "Lower Priority": "#6b778c"
            };
            
            // eslint-disable-next-line no-unused-vars
            const color = categoryColors[survey.category] || '#6b778c';
            const techScore = parseFloat(survey.y).toFixed(1);
            const roiScore = parseFloat(survey.x).toFixed(1);
            const strategicScore = parseFloat(survey.r / 3).toFixed(1);
            
            const fullDescription = survey.opportunity_description || "No description available";
            const truncatedDescription = getTruncatedDescription(fullDescription);
            const isExpanded = expandedDescriptions[index];
            const shouldShowToggle = fullDescription.split(' ').length > 16;
            
            return (
              <div key={index} className="opportunity-card">
                <div className="opportunity-card-content">
                  <div className="opportunity-card-title">
                    {survey.opportunity_name || "AI Opportunity"}
                  </div>
                  
                  <div className="opportunity-card-description">
                    {isExpanded ? fullDescription : truncatedDescription}
                    {shouldShowToggle && (
                      <button 
                        onClick={() => toggleDescription(index)}
                        className="description-toggle-btn"
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#0066cc',
                          cursor: 'pointer',
                          fontSize: '13px',
                          fontWeight: '600',
                          marginLeft: '5px',
                          padding: '0',
                          textDecoration: 'underline'
                        }}
                      >
                        {isExpanded ? 'Show less' : 'Read more'}
                      </button>
                    )}
                  </div>

                  <div className="opportunity-card-metrics">
                    <div className="metrics-column">
                      <div className="metrics-label">ROI</div>
                      <div className="metrics-value">{roiScore >= 0 ? `+${roiScore}` : roiScore}</div>
                    </div>
                    
                    <div className="metrics-column">
                      <div className="metrics-label">Tech</div>
                      <div className="metrics-value">{techScore >= 0 ? `+${techScore}` : techScore}</div>
                    </div>
                    
                    <div className="metrics-column">
                      <div className="metrics-label">Strategic</div>
                      <div className="metrics-value">{strategicScore}</div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className={`chart-container ${isPortfolio ? 'portfolio-chart' : ''}`}>
      <div className="chart-header">
        <div className="chart-header-bg">
          <h2>
            {selectedSubmissions && selectedSubmissions.length > 0 
              ? `Combined Chart (${selectedSubmissions.length} Surveys)` 
              : getOpportunityName()}
          </h2>
          <div className="chart-subtitle">Opportunity Survey Analysis</div>
          <div className="chart-legend-info">
            • X-axis: ROI Potential (-10 to +10)<br />
            • Y-axis: Tech Feasibility (-10 to +10)<br />
            • Bubble Size: Strategic Value (1-10)
          </div>
        </div>
      </div>
      
      <div ref={chartContainerRef} className="chart-canvas-container"></div>
      
      {/* Category Legend */}
      {chartData && chartData.categorized_data && (
        <div className="category-legend" style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          flexWrap: 'wrap', 
          gap: '15px', 
          margin: '20px 0',
          padding: '15px',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px'
        }}>
          {Object.entries(chartData.categorized_data).map(([category, items]) => {
            if (items.length === 0) return null;
            const categoryColors = {
              "Quick Wins": "#36b37e",
              "Strategic Challenges": "#ff5630", 
              "Technical Experiments": "#0065ff",
              "Lower Priority": "#6b778c"
            };
            const color = categoryColors[category] || '#6b778c';
            
            return (
              <div key={category} style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 12px',
                backgroundColor: 'white',
                borderRadius: '6px',
                border: `2px solid ${color}`,
                fontSize: '14px',
                fontWeight: '500'
              }}>
                <div style={{
                  width: '16px',
                  height: '16px',
                  borderRadius: '50%',
                  backgroundColor: color
                }}></div>
                <span style={{ color: color }}>{category}</span>
                <span style={{ 
                  backgroundColor: color, 
                  color: 'white', 
                  padding: '2px 6px', 
                  borderRadius: '10px',
                  fontSize: '12px',
                  fontWeight: 'bold'
                }}>
                  {items.length}
                </span>
              </div>
            );
          })}
        </div>
      )}
      
      {/* Survey Cards - Show for both portfolio and single assessment views */}
      {renderSurveyCards()}
      
      {/* Detailed Scores - Only show for individual submission charts, not company charts or combined charts */}
      {!isPortfolio && !isResultsView && !isPublic && !companyDomain && !selectedSubmissions && scores && (
        <div className="scores-container">
          <div className="score-card tech-score">
            <h3>Tech Feasibility Assessment</h3>
            <div className="score-value">+{scores.tech}</div>
            <ul className="score-details">
              <li>Predictable patterns in process flow (~5)</li>
              <li>Cloud services acceptable for implementation (~4)</li>
              <li>APIs available for required systems (+6)</li>
              <li>Well-documented requirements</li>
            </ul>
            <div className="score-rationale">
              <strong>Rationale:</strong> 
              <p>High automation potential with structured templates and established data sources.</p>
            </div>
          </div>
          
          <div className="score-card roi-score">
            <h3>ROI Potential</h3>
            <div className="score-value">+{scores.roi}</div>
            <ul className="score-details">
              <li>High daily transaction volume</li>
              <li>25% time savings expected</li>
              <li>Moderate change management needed</li>
              <li>Significant risk reduction potential (~9)</li>
            </ul>
            <div className="score-rationale">
              <strong>Rationale:</strong> 
              <p>Significant cost savings from automation and substantial risk reduction.</p>
            </div>
          </div>
          
          <div className="score-card strategic-score">
            <h3>Strategic Value</h3>
            <div className="score-value">{scores.strategic}</div>
            <ul className="score-details">
              <li>Direct alignment with regulatory directives</li>
              <li>Major brand protection from compliance</li>
              <li>Core operational risk management</li>
            </ul>
            <div className="score-rationale">
              <strong>Rationale:</strong> 
              <p>Mission-critical for regulatory standing and operational excellence.</p>
            </div>
          </div>
        </div>
      )}
      
      {/* Only show download buttons for single opportunity charts (not portfolio) and not in results view */}
      {!isPortfolio && !isResultsView && (
        <div className="chart-actions">
          <button onClick={downloadChartAsPDF} className="download-btn download-btn-pdf">
            Download as PDF
          </button>
          <button onClick={downloadChartAsHTML} className="download-btn download-btn-html">
    
            Download as HTML
          </button>
        </div>
      )}
    </div>
  );
};

// Export the chart generation function for use by other components
export { OpportunityChart as default };

// Export utility function for generating charts for emails
export const generateChartForEmail = (submissionId, opportunityName) => {
  // This will be called from SurveyForm after successful submission
  // We need the chart data, so this needs to be triggered from within OpportunityChart
  return new Promise((resolve, reject) => {
    // This function will be used by SurveyForm to trigger chart generation
    // The actual implementation will be in the OpportunityChart component
    reject(new Error("This function should be called from within OpportunityChart component"));
  });
};